import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Check } from "lucide-react";
import { MaskLines, Overline, FadeUp } from "./Reveal";
import { CONTACT, SERVICE_OPTIONS } from "./data";

const FORM_ENDPOINT = "https://formsubmit.co/ajax/info@hairboutiqueshop.it";
const EMPTY = { name: "", phone: "", email: "", service: "", date: "", time_slot: "", notes: "" };
const today = new Date().toISOString().slice(0, 10);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-ivory/70">{label}</span>
    {children}
  </label>
);

export const Booking = () => {
  const [f, setF] = useState(EMPTY);
  const [state, setState] = useState("idle");
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState("loading");
    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          _subject: `Nuova richiesta prenotazione — ${f.name}`,
          _template: "table",
          _captcha: "false",
          Nome: f.name,
          Telefono: f.phone,
          Email: f.email || "—",
          Servizio: f.service,
          Data: f.date,
          "Fascia oraria": f.time_slot,
          Note: f.notes || "—",
        }),
      });
      if (!res.ok) throw new Error("Form error");
      setState("done");
      setF(EMPTY);
      toast.success("Richiesta inviata! Ti ricontatteremo per confermare l'appuntamento.");
    } catch (err) {
      setState("idle");
      toast.error(`Controlla i dati inseriti oppure chiamaci al ${CONTACT.phone}.`);
    }
  };

  return (
    <section id="prenota" data-testid="booking-section" className="relative py-24 sm:py-36 bg-obsidian-2 overflow-hidden">
      <div className="absolute -right-40 top-20 w-[520px] h-[520px] rounded-full bg-gold/10 blur-[140px] pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Overline>Prenota</Overline>
          <h2 className="mt-6 font-display text-5xl sm:text-7xl leading-[0.95] text-ivory">
            <MaskLines lines={["Il tuo", <em key="m" className="text-gold-grad">momento</em>, "ti aspetta."]} />
          </h2>
          <p className="mt-8 max-w-md text-sm sm:text-base text-ivory/75 leading-relaxed">
            Compila la richiesta: ti ricontattiamo noi per confermare giorno e orario. Preferisci scegliere subito? Prenota in tempo reale su Treatwell.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-4">
            <a data-testid="treatwell-external-link" href={CONTACT.treatwell} target="_blank" rel="noreferrer" className="btn-ghost">
              Prenota su Treatwell <ArrowUpRight className="w-4 h-4" />
            </a>
            <a data-testid="booking-whatsapp-link" href={CONTACT.whatsapp} target="_blank" rel="noreferrer" className="btn-ghost">
              Scrivici su WhatsApp <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>

        <FadeUp className="lg:col-span-7">
          <form data-testid="booking-form" onSubmit={submit} className="glass rounded-[28px] p-6 sm:p-10 grid sm:grid-cols-2 gap-x-6 gap-y-7">
            <Field label="Nome e cognome *">
              <input data-testid="booking-form-input-name" required minLength={2} value={f.name} onChange={set("name")} className="field" placeholder="Maria Rossi" />
            </Field>
            <Field label="Telefono *">
              <input data-testid="booking-form-input-phone" required type="tel" minLength={6} pattern="[0-9+\s().-]+" value={f.phone} onChange={set("phone")} className="field" placeholder="+39 ..." />
            </Field>
            <Field label="Email">
              <input data-testid="booking-form-input-email" type="email" value={f.email} onChange={set("email")} className="field" placeholder="nome@email.it" />
            </Field>
            <Field label="Servizio *">
              <select data-testid="booking-form-select-service" required value={f.service} onChange={set("service")} className="field">
                <option value="" disabled>Seleziona…</option>
                {SERVICE_OPTIONS.map((o) => <option key={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Data desiderata *">
              <input data-testid="booking-form-input-date" required type="date" min={today} value={f.date} onChange={set("date")} className="field [color-scheme:dark]" />
            </Field>
            <Field label="Fascia oraria *">
              <select data-testid="booking-form-select-time" required value={f.time_slot} onChange={set("time_slot")} className="field">
                <option value="" disabled>Seleziona…</option>
                <option>Mattina</option>
                <option>Pomeriggio</option>
                <option>Indifferente</option>
              </select>
            </Field>
            <Field label="Note">
              <textarea data-testid="booking-form-input-notes" rows={3} maxLength={1000} value={f.notes} onChange={set("notes")} className="field resize-none" placeholder="Lunghezza, colore attuale, idee…" />
            </Field>
            <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-5 pt-2">
              <p className="text-xs text-ivory/60 max-w-xs">I dati sono usati solo per ricontattarti riguardo all'appuntamento.</p>
              <button data-testid="booking-form-submit-button" disabled={state === "loading"} className="btn-gold justify-center disabled:opacity-60">
                {state === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : state === "done" ? <Check className="w-4 h-4" /> : null}
                {state === "done" ? "Richiesta inviata" : "Invia richiesta"}
                {state === "idle" && <ArrowUpRight className="w-4 h-4" />}
              </button>
            </div>
          </form>
        </FadeUp>
      </div>
    </section>
  );
};
